import React, { useCallback, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { UserEntity } from 'Models/Entities';
import {
	UserAddOnPriceRow,
	UserPriceRow,
	mapUserPricingToRows,
	rowToAddOnEntity,
	rowToPriceEntity,
} from 'Modules/UserAccount/Pricing/userPricingRows';
import { useUserDisplayNamesStore } from 'Global/User/UserDisplayNameStore';
import alertToast from 'Util/ToastifyUtils';
import { FormErrors } from 'Util/_HumanWritten/Errors/FormErrors';
import { ScrollToError } from 'Validators/Functions/HumanWritten/ScrollToError';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import {
	isEmailValid,
	isPhoneValid,
	validateForm,
} from 'Views/Components/_HumanWritten/AccountDetails/AccountFields/AccountFieldsValidation';
import handleNavigationAway from 'Views/Components/_HumanWritten/AccountDetails/HandleNavigationAway';
import { useAccountNavigationAway } from 'Hooks/useAccountNavigationAway';

/** Everything the account edit session stages, owned by react-hook-form. */
export type AccountFormValues = {
	firstName: string;
	lastName: string;
	/** Staged: an email change is confirmed and sent as a change-email request, never saved directly. */
	email: string;
	/** Staged like email, but (unlike email) applied to the save payload once it passes the server check. */
	phone: string | null;
	postcode: string;
	companyName: string;
	userDisplayNameId: string | null;
	/** Edited on the Pricing tab (and cleared with the type); null when the user has none. */
	additionalInformation: string | null;
	/** The Pricing tab's rows as plain objects — entities only reappear at the save boundary. */
	prices: UserPriceRow[];
	addOnsPrices: UserAddOnPriceRow[];
};

export function mapUserToFormValues(user: UserEntity): AccountFormValues {
	const pricing = mapUserPricingToRows(user);
	return {
		firstName: user.firstName ?? '',
		lastName: user.lastName ?? '',
		email: user.email ?? '',
		phone: user.phone ?? null,
		postcode: user.postcode ?? '',
		companyName: user.companyName ?? '',
		userDisplayNameId: user.userDisplayNameId ?? null,
		additionalInformation: user.additionalInformation ?? null,
		prices: pricing.prices,
		addOnsPrices: pricing.addOnsPrices,
	};
}

export interface PerformSaveContext {
	emailHasChanged: boolean;
	/** Transient entity carrying the merged form + pricing state, shaped for saveUser. */
	submitUser: UserEntity;
	/** The staged email from the form — pages send it through their change-email request. */
	newEmail: string;
	/**
	 * Whether this save changes what the customer is charged: a Pricing-tab edit, or a customer-type change
	 * (which clears their own prices and moves them onto the new type's). Captured before the save, since
	 * clearChangesMade resets the flag it is read from.
	 */
	pricesChanged: boolean;
	/** Call once the primary save has committed so the navigation-away guard stands down. */
	clearChangesMade: () => void;
}

export interface AccountEditSessionOptions {
	user: UserEntity;
	refetchUser: () => void;
	/**
	 * The page-specific save side-effects (customer self-save vs staff on-behalf save). Runs
	 * after validation, the phone/email server checks and — when the email changed — the
	 * confirm modal. A throw here surfaces as the generic save-failure toast.
	 */
	performSave: (context: PerformSaveContext) => Promise<void>;
	/** Where to send focus when client-side validation fails (e.g. switch to the pricing tab). */
	onValidationErrors?: (errors: FormErrors<UserEntity>) => void;
}

export interface AccountEditSession {
	form: UseFormReturn<AccountFormValues>;
	errors: FormErrors<UserEntity>;
	setErrors: (errors: FormErrors<UserEntity>) => void;
	saving: boolean;
	changesMade: boolean;
	/**
	 * Arms the navigation-away guard for pricing-row edits. Those go through setValue without
	 * shouldDirty — seeding placeholder rows shares the same write path and must NOT read as a
	 * user edit — so the Pricing tab flags real edits explicitly instead (legacy setNeedConfirm).
	 */
	markChanged: () => void;
	handleSave: () => Promise<void>;
	/** Resolves true when the edits were discarded (or there were none) — false = user kept editing. */
	cancelEdit: () => Promise<boolean>;
}

/**
 * The account-page edit session, shared by CustomerAccountInner and UserDetailInner: one
 * react-hook-form over everything the pages edit — the scalar account fields (email/phone
 * staged in the form, exactly like the legacy specialState — the fetched entity stays untouched
 * until submit) plus the Pricing tab's rows as plain objects. Validation errors, the dirty
 * state feeding the navigation-away guard, and the full save flow live here too. Cancel throws
 * edits away by refetching, which remounts the page on fresh data.
 *
 * Failure semantics (deliberate): only the email-change confirm modal's rejection is treated
 * as a cancel; failures thrown by performSave surface as an error toast and never a success
 * one. Pages own their success toasts inside performSave, after everything they await.
 */
export function useAccountEditSession({
	user,
	refetchUser,
	performSave,
	onValidationErrors,
}: AccountEditSessionOptions): AccountEditSession {
	const intl = useIntl();
	const { userDisplayNames } = useUserDisplayNamesStore();

	const form = useForm<AccountFormValues>({ defaultValues: mapUserToFormValues(user) });

	const [errors, setErrors] = useState<FormErrors<UserEntity>>({});
	const [saving, setSaving] = useState(false);
	const [pricingChanged, setPricingChanged] = useState(false);

	// dirtyFields, NOT isDirty: isDirty is a whole-form deep-equal against defaultValues, and
	// the Pricing tab seeds placeholder rows into `prices` on mount (without shouldDirty). Any
	// later shouldDirty write makes isDirty recompute over the seeded rows and latch true for
	// the rest of the session — arming the navigation guard with no real edits, even after a
	// revert. dirtyFields is per-field, only ever marked by shouldDirty writes, and unmarks on
	// revert, so the seed can't arm it. (Reading it here subscribes the page to updates.)
	const changesMade = Object.keys(form.formState.dirtyFields).length > 0 || pricingChanged;

	// Discarding must disarm the guard SYNCHRONOUSLY. The refetch that follows does remount the
	// page onto fresh data, but only once it lands — and the navigation blocker re-checks
	// needConfirm when it re-attempts the blocked navigation, well inside that window. With only
	// the refetch, the blocker re-blocks its own proceed() and the discard modal loops (the
	// legacy useAsync wrapper unmounted the page synchronously on refresh(), which hid this).
	const discardChanges = useCallback(() => {
		form.reset();
		setPricingChanged(false);
		setErrors({});
		refetchUser();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refetchUser]);

	useAccountNavigationAway({
		needConfirm: changesMade,
		confirmationFunction: discardChanges,
	});

	const handleSave = async () => {
		if (saving) {
			return;
		}
		setSaving(true);

		const values = form.getValues();
		const emailHasChanged = user.email !== values.email;

		// Materialise the form onto a transient entity — the shape the legacy copiedUser had at
		// this point. Email is deliberately NOT applied: the account save keeps the original
		// address; the change goes through the page's change-email request. The type's linked
		// object rides along because validateForm's company/additional-information rules and
		// saveUser's payload shaping read it. (The identity role swap for a type change is now
		// handled server-side by the save-account endpoint.)
		const submitUser = new UserEntity(user);
		submitUser.firstName = values.firstName;
		submitUser.lastName = values.lastName;
		// The form models "no phone on the account" as null (the phone input only ever hands back a
		// string, so null means the field was never touched). Leaving the clone's inherited value in
		// place then sends the same empty phone as before, rather than turning it into ''.
		if (values.phone !== null) {
			submitUser.phone = values.phone;
		}
		submitUser.postcode = values.postcode;
		submitUser.companyName = values.companyName;
		// Deliberately not assigned. Marketing consent is edited on the Communications tab and is
		// tri-state — null means the customer has never been asked, and the Brevo sync treats that as
		// "skip" rather than consent. Writing it from here flattened null to false on every save of an
		// unrelated field, recording a decision nobody made.
		submitUser.additionalInformation = values.additionalInformation ?? undefined;
		submitUser.userDisplayNameId = values.userDisplayNameId ?? undefined;
		submitUser.userDisplayName =
			values.userDisplayNameId == null
				? undefined
				: (userDisplayNames.find(x => x.id === values.userDisplayNameId) ??
					(user.userDisplayNameId === values.userDisplayNameId ? user.userDisplayName : undefined));
		submitUser.prices = (values.prices ?? []).map(rowToPriceEntity);
		submitUser.addOnsPrices = (values.addOnsPrices ?? []).map(rowToAddOnEntity);

		const newErrors = validateForm(submitUser, { email: values.email, phone: values.phone ?? '' }, intl);
		setErrors(newErrors);
		if (Object.keys(newErrors).length > 0) {
			onValidationErrors?.(newErrors);
			ScrollToError('end');
			setSaving(false);
			return;
		}

		const res = await Promise.all([
			isPhoneValid(user.phone, values.phone ?? ''),
			isEmailValid(user.email, values.email),
		]);
		if (res.includes(false)) {
			setSaving(false);
			return;
		}

		if (emailHasChanged) {
			// Only the modal's rejection means "user cancelled" — it must not swallow
			// failures from the save itself, so the save is awaited outside this try.
			try {
				await newConfirmModal(
					'Change email address?',
					<div className="spaced-message">
						<p>
							From: <strong className="brand-colour">{user.email}</strong>
						</p>
						<p>
							To: <strong className="brand-colour">{values.email}</strong>
						</p>
						<p>Are you sure you want to proceed with these changes?</p>
					</div>,
				);
			} catch {
				setSaving(false);
				return;
			}
		}

		const pricesChanged = pricingChanged || (user.userDisplayNameId ?? null) !== (values.userDisplayNameId ?? null);

		try {
			await performSave({
				emailHasChanged,
				submitUser,
				newEmail: values.email,
				pricesChanged,
				clearChangesMade: () => {
					form.reset(values);
					setPricingChanged(false);
				},
			});
		} catch (e) {
			alertToast('Could not save information', 'error');
		} finally {
			setSaving(false);
		}
	};

	const cancelEdit = async () => {
		return handleNavigationAway(changesMade, discardChanges);
	};

	return {
		form,
		errors,
		setErrors,
		saving,
		changesMade,
		markChanged: () => setPricingChanged(true),
		handleSave,
		cancelEdit,
	};
}
